/**
 * Context Index & Provider Wrapper
 * Central export point for all contexts and combined provider
 */

import { type ReactNode } from 'react';

// ============================================================
// RE-EXPORTS
// ============================================================

// Network Context
export {
  NetworkProvider,
  useNetwork,
  useNetworkNodes,
  useNetworkConnections,
  useNetworkSelection,
  useNetworkHistory,
  useNetworkStats,
  type NetworkContextValue,
  type NetworkProviderProps,
} from './NetworkContext';

// Pyodide Context
export {
  PyodideProvider,
  usePyodideContext,
  usePyodideStatus,
  usePowerFlow,
  usePyodideConsole,
  usePowerFlowResults,
  type PyodideContextValue,
  type PyodideProviderProps,
  type ConsoleEntry,
  type PowerFlowOptions,
} from './PyodideContext';

// Auth Context
export {
  AuthProvider,
  useAuth,
} from './AuthContext';

// ============================================================
// COMBINED PROVIDER
// ============================================================

import { NetworkProvider } from './NetworkContext';
import { PyodideProvider } from './PyodideContext';
import type { Node, Connection } from '@/types';

/** Combined app provider props */
export interface AppProviderProps {
  children: ReactNode;

  // Network options
  initialNodes?: Node[];
  initialConnections?: Connection[];
  networkStorageKey?: string;

  // Pyodide options
  autoInitializePyodide?: boolean;
  maxConsoleEntries?: number;
}

/**
 * AppProvider component
 * Combines NetworkProvider + PyodideProvider into a single wrapper.
 * Canvas/map rendering is handled by NetworkDiagram (MapLibre + deck.gl).
 */
export function AppProvider({
  children,
  // Network options
  initialNodes = [],
  initialConnections = [],
  networkStorageKey = 'MangoGrid-network',
  // Pyodide options
  autoInitializePyodide = false,
  maxConsoleEntries = 100,
}: AppProviderProps): JSX.Element {
  return (
    <NetworkProvider
      initialNodes={initialNodes}
      initialConnections={initialConnections}
      storageKey={networkStorageKey}
    >
      <PyodideProvider
        autoInitialize={autoInitializePyodide}
        maxConsoleEntries={maxConsoleEntries}
      >
        {children}
      </PyodideProvider>
    </NetworkProvider>
  );
}

// ============================================================
// UTILITY TYPES
// ============================================================

/** All context values combined */
export interface AllContexts {
  network: import('./NetworkContext').NetworkContextValue;
  pyodide: import('./PyodideContext').PyodideContextValue;
}

// ============================================================
// DEFAULT EXPORT
// ============================================================

export default AppProvider;

